import React, { useMemo, useState } from 'react';
import { Tabs } from '@arco-design/web-react';
import { IconClose } from '@arco-design/web-react/icon';
import { observer } from 'mobx-react-lite';
import { useMount } from 'ahooks';
import Empty from '@/components/Empty';
import SpinContainer from '@/components/SpinContainer';
import TodoItem from './components/TodoItem';
import styles from './index.module.less';
import { useTodoData } from '@/utils/hooks/useTodoData';
import TodoAvatarIcon from '@/assets/home/home-todo-avatar.png';
import { useStore } from '@/store';
import {
  AuditBusinessCode,
  AuditBusinessCodeText,
} from '@/constants/business/audit';
import UserAvatar from '@/components/UserAvatar';
import {
  PerformanceMonthStageText,
  PerformanceStage,
  PerformanceStageText,
  PerformanceType,
} from '@/constants/business/performance';

interface ITodoListProps {
  orgId: string;
  isShowTitle?: boolean;
  autoFetch?: boolean; // 是否自动获取数据，默认true
  onCancel?: () => void;
  showClose?: boolean;
  closeOnItemClick?: boolean;
}

export interface ITodoItem {
  avatar: React.ReactNode;
  title: string;
  content: React.ReactNode;
  tip?: string;
  url: string;
  ctime?: number;
  category:
    | 'leave'
    | 'hr'
    | 'recruit'
    | 'attendance'
    | 'performance'
    | 'salary';
}

const { TabPane } = Tabs;

const TodoList = observer(
  ({
    orgId,
    isShowTitle = true,
    autoFetch = true,
    onCancel,
    showClose = false,
    closeOnItemClick = false,
  }: ITodoListProps) => {
    const [activeTab, setActiveTab] = useState('all');
    const { loading, todo, fetchTodoData } = useTodoData();

    useMount(() => {
      if (autoFetch) {
        fetchTodoData();
      }
    });

    const { member } = useStore('user');
    const memberId = member?.memberId;

    // 分类统计
    const categoryStats = useMemo(() => {
      if (!todo)
        return {
          all: 0,
          leave: 0,
          hr: 0,
          recruit: 0,
          attendance: 0,
          performance: 0,
          salary: 0,
        };

      const stats = {
        all: 0,
        leave: 0,
        hr: 0,
        recruit: 0,
        attendance: 0,
        performance: 0,
        salary: 0,
      };

      // 统计各类待办数量
      stats.leave =
        (todo.leaveTodo?.length || 0) +
        (todo.outBusinessTodo?.length || 0) +
        (todo.outOfficialTodo?.length || 0);
      stats.hr =
        (todo.applyTodo?.length || 0) +
        (todo.entryTodo?.length || 0) +
        (todo.trialTodo?.length || 0);
      stats.recruit =
        (todo.recruitTodo?.length || 0) + (todo.positionApplyTodo?.length || 0);
      stats.attendance =
        (todo.replaceCardTodo?.length || 0) +
        (todo.overtimeTodo?.length || 0) +
        (todo.shiftApplyTodo?.length || 0);
      stats.performance = todo.perTodo?.length || 0;
      stats.salary = todo.salaryIncreaseTodo?.length || 0;
      stats.all =
        stats.leave +
        stats.hr +
        stats.recruit +
        stats.attendance +
        stats.performance +
        stats.salary;

      return stats;
    }, [todo]);

    // 处理待办数据
    const processedTodos = useMemo(() => {
      if (!todo) return [];

      const todos: ITodoItem[] = [];

      // 绩效待办
      (todo.perTodo || []).forEach((item) => {
        const single =
          item.stageCode === PerformanceStage.SelfEvaluation ||
          item.stageCode === PerformanceStage.Confirm;
        const isMonth = item.performance?.type === PerformanceType.Month;
        const stageCodeText = item.stageCode
          ? isMonth
            ? PerformanceMonthStageText[item.stageCode]
            : PerformanceStageText[item.stageCode]
          : '--';
        const isJumpDetail =
          item.stageCode === PerformanceStage.SelfEvaluation ||
          item.stageCode === PerformanceStage.Confirm;
        const url = isMonth
          ? `/org/${orgId}/performance/month`
          : isJumpDetail
          ? `/org/${orgId}/performance/my/detail?performance_id=${item.performance?.id}&stage=${item.stageCode}&account_id=${memberId}`
          : `/org/${orgId}/performance/home/todo?performance_id=${item.performance?.id}&stage=${item.stageCode}`;

        todos.push({
          avatar: <img src={TodoAvatarIcon} className={styles.userRound} />,
          title: `【${stageCodeText}】待处理`,
          content: `请您尽快完成【${item.performance?.name}】的【${stageCodeText}】`,
          tip: single
            ? undefined
            : `待处理${item?.todoCount}人 已处理${item?.doneCount}人 共${
                item?.totalCount || 0
              }人`,
          url,
          category: 'performance',
          // ctime: item?.ctime || 0,
          ctime: item?.lastTodoTime,
        });
      });

      // 请假待办
      (todo.leaveTodo || []).forEach((item) => {
        const userName = item.creator?.name || '';
        const leaveName =
          AuditBusinessCodeText[item.applyTypeCode as AuditBusinessCode] ||
          '请假申请';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的【${leaveName}】申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的【${leaveName}】【${item.duration}天】申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/leave/manage?tab=1`,
          category: 'leave',
          ctime: item.ctime,
        });
      });

      // 出差待办
      (todo.outBusinessTodo || []).forEach((item) => {
        const userName = item.creator?.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的出差申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的${
                item.destination ? `【${item.destination}】` : ''
              }【${item.duration}天】出差申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/leave/out-business?tab=1`,
          category: 'leave',
          ctime: item.ctime,
        });
      });

      // 公出待办
      (todo.outOfficialTodo || []).forEach((item) => {
        const userName = item.creator?.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的公出申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的【${item.duration}天】公出申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/leave/out-official?tab=1`,
          category: 'leave',
          ctime: item.ctime,
        });
      });

      // 补卡待办
      (todo.replaceCardTodo || []).forEach((item) => {
        const userName = item.applyer?.name || '';
        const date = item.recordDate
          ? new Date(item.recordDate * 1000).toLocaleDateString('zh-CN')
          : '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的【${date}】补卡待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的【${date}】的补卡申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/attendance/my-attendance?tab=replaceCard`,
          category: 'attendance',
          ctime: item.ctime,
        });
      });

      // 入职待办
      (todo.entryTodo || []).forEach((item) => {
        const userName = item.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的入职申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的入职申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/people/entry/todo?account_id=${item.accountId}`,
          category: 'hr',
          ctime: item.ctime,
        });
      });

      // 人事申请待办
      (todo.applyTodo || []).forEach((item) => {
        const userName = item.account?.name || '';
        let typeName = '';
        let url = '';

        if (item.applyType === 1) {
          url = `/org/${orgId}/people/staff-apply/resign/${item.auditId}`;
          typeName = '离职';
        } else if (item.applyType === 2) {
          url = `/org/${orgId}/people/staff-apply/regular/${item.auditId}`;
          typeName = '转正';
        } else if (item.applyType === 3) {
          url = `/org/${orgId}/people/staff-apply/transfer/${item.auditId}`;
          typeName = '调动';
        } else if (item.applyType === 4) {
          url = `/org/${orgId}/people/staff-apply/collect/${item.auditId}`;
          typeName = '信息采集';
        }

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】${typeName}的【${item.circleContent}】待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】${typeName}的【${item.circleContent}】`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: url,
          category: 'hr',
          ctime: item.auditUtime,
        });
      });

      // 加班待办
      (todo.overtimeTodo || []).forEach((item) => {
        const userName = item.creator?.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的加班申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的加班申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/attendance/overtime/detail/${item.auditId}`,
          category: 'attendance',
          ctime: item.ctime,
        });
      });

      // 招聘待办
      (todo.recruitTodo || []).forEach((item) => {
        const userName = item.candidateAccountName || '';
        const stageMap = {
          screening: '简历筛选',
          interview: '面试评价',
          offer: 'offer 审批',
        };
        const routeMap = {
          screening: 'resume-screening',
          interview: 'interview',
          offer: 'candidate',
        };

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${stageMap[item.stage as keyof typeof stageMap]}】待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的【${
                stageMap[item.stage as keyof typeof stageMap]
              }】`}
            </>
          ),
          tip: item.utime
            ? new Date(item.utime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/recruitment/${
            routeMap[item.stage as keyof typeof routeMap]
          }?account_id=${item.candidateAccountId || ''}&stage=${
            item.stage
          }&interview_id=${item.interviewId}`,
          category: 'recruit',
          ctime: item.utime,
        });
      });

      // HC申请待办
      (todo.positionApplyTodo || []).forEach((item) => {
        const userName = item.creator?.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【${userName}】的HC申请待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的HC申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/recruitment/hc/detail/${item.auditId}`,
          category: 'recruit',
          ctime: item.ctime,
        });
      });

      // 调薪待办
      (todo.salaryIncreaseTodo || []).forEach((item) => {
        const userName = item.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【调薪申请】待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的调薪申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/salary/increase/apply/${item.auditId}`,
          category: 'salary',
          ctime: item.ctime,
        });
      });

      // 试用期待办
      (todo.trialTodo || []).forEach((item) => {
        const userName = item.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【结束试岗】待处理`,
          content: (
            <>
              {`【${userName}】的试岗期已到，`}
              请您尽快<span className={styles.highLight}>处理</span>
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/people/trial?key=${item.name}`,
          category: 'hr',
          ctime: item.ctime,
        });
      });

      // 调班待办
      (todo.shiftApplyTodo || []).forEach((item) => {
        const userName = item?.shiftMember?.name || '';

        todos.push({
          avatar: <UserAvatar name={userName} size={40} />,
          title: `【调班申请】待处理`,
          content: (
            <>
              请您尽快<span className={styles.highLight}>处理</span>
              {`【${userName}】的调班申请`}
            </>
          ),
          tip: item.ctime
            ? new Date(item.ctime * 1000).toLocaleString('zh-CN')
            : '',
          url: `/org/${orgId}/attendance/shift-transfer/detail/${item.auditId}`,
          category: 'attendance',
          ctime: item.ctime,
        });
      });

      return todos.sort((a, b) => (a.ctime || 0) - (b.ctime || 0));
    }, [todo, orgId]);

    // 根据当前选中的标签页过滤数据
    const filteredTodos = useMemo(() => {
      if (activeTab === 'all') {
        return processedTodos;
      }
      return processedTodos.filter((item) => item.category === activeTab);
    }, [processedTodos, activeTab]);

    return (
      <div className={styles.todoListContainer}>
        {showClose && (
          <button
            type="button"
            className={styles.closeButton}
            aria-label="关闭待办面板"
            onClick={onCancel}
          >
            <IconClose />
          </button>
        )}
        {isShowTitle && <div className={styles.title}>我的待办</div>}

        <Tabs
          activeTab={activeTab}
          onChange={setActiveTab}
          className={styles.tabs}
          size="large"
        >
          <TabPane key="all" title={`全部 ${categoryStats.all}`} />
          <TabPane key="leave" title={`假勤 ${categoryStats.leave}`} />
          <TabPane key="hr" title={`人事 ${categoryStats.hr}`} />
          <TabPane key="recruit" title={`招聘 ${categoryStats.recruit}`} />
          <TabPane
            key="attendance"
            title={`考勤 ${categoryStats.attendance}`}
          />
          <TabPane
            key="performance"
            title={`绩效 ${categoryStats.performance}`}
          />
          <TabPane key="salary" title={`薪资 ${categoryStats.salary}`} />
        </Tabs>

        <SpinContainer loading={loading} style={{ flex: 1, overflowY: 'auto' }}>
          {!!filteredTodos.length && (
            <div className={styles.todoItems}>
              {filteredTodos.map((item, index) => (
                <TodoItem
                  key={index}
                  avatar={item.avatar}
                  title={item.title}
                  content={item.content}
                  tip={item.tip}
                  url={item.url}
                  onCancel={closeOnItemClick ? onCancel : undefined}
                />
              ))}
            </div>
          )}

          {!loading && !filteredTodos.length && (
            <Empty
              text={'暂无待办事项'}
              type={'noData'}
              style={{ marginTop: 120 }}
            />
          )}
        </SpinContainer>
      </div>
    );
  }
);

export default TodoList;
